<template>
  <article class="product" :class="wrapperClasses" :id="wrapperId" @click="showFull">
    <main-media v-if="hasMainMedia" :media="mainMedia" :sizes="mainImageSizes"></main-media>
    <div class="main-info">
      <div class="main-text">
        <h3 class="label">{{labels.edition_info}}</h3>
        <div class="body" v-html="description"></div>
      </div>
      <div class="product-data">
        <h3 class="label">{{labels.purchase}}</h3>
        <p class="price" :title="basePrice|currencyExclVAT">{{currentPrice}}</p>
        <div v-if="hasOptions" class="options" @click="preCheckOptions">
          <div
            v-for="(opt, optIndex) in options"
            class="opt-group"
            :key="optionKey('opt', productId, optIndex)"
          >
            <div class="opt-label">{{opt.name}}</div>
            <div class="opt-items">
              <div
                v-for="(choice, choiceIndex) in opt.choices"
                :key="optionKey('opt', productId, optIndex, choiceIndex)"
                :id="optionKey('opt',productId, optIndex, choiceIndex)"
                class="opt-item"
                :class="{'selected': selectedOptions[optIndex] == choice.text}"
              >
                <input
                  :name="optionKey(productId, optIndex)"
                  :id="optionKey('choice',productId, optIndex,choiceIndex)"
                  :value="choice.text"
                  v-model="selectedOptions[optIndex]"
                  type="radio"
                  class="selector radio"
                />
                <label
                  :for="optionKey('choice', productId, optIndex,choiceIndex)"
                  :title="calcExtra(choice.priceModifier)"
                >{{choice.text}}</label>
              </div>
            </div>
          </div>
        </div>
        <ul v-if="errorMessages.length > 0" class="error-messages">
          <li
            v-for="(msg, msgIndex) in errorMessages"
            :key="optionKey('error', productId, msgIndex)"
          >{{msg}}</li>
        </ul>
        <div v-if="available" class="add-to-cart selectable" :class="buttonClasses">
          <span class="label" @click="addEcwidProductFirst(productId)">{{addToCartText}}</span>
          <span
            v-if="addedToCart"
            @click="addEcwidProduct(productId)"
            class="icon-plus quantity-control plus-1"
            :title="labels.add_one_more"
          ></span>
          <span class="quantity" v-if="quantity > 0">{{quantity}}</span>
          <span
            v-if="quantity > 0"
            class="icon-minus quantity-control minus-1"
            :title="labels.remove_one"
            @click="subtractEcwidProduct(productId)"
          ></span>
          <span
            v-if="quantity > 1"
            class="icon-close delete icon-only on-hover"
            :title="labels.none_required"
            @click="removeEcwidProduct(productId)"
          ></span>
        </div>
        <div v-else class="add-to-cart not-available">
          <p>Sold out</p>
        </div>
      </div>
    </div>
    <section-list v-if="full && hasSections" :sections="sections" :id="id"></section-list>
    <slot></slot>
  </article>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import MainMedia from "./MainMedia";
import SectionList from "./SectionList";
import filters from "../mixins/filters";
import config from "../config/config";
import { hasEcwidCart, getCart } from "../api/ecwid";

export default {
  name: "Product",
  components: {
    MainMedia,
    SectionList,
  },
  props: {
    product: {
      type: Object,
    },
    full: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [filters],
  data() {
    return {
      id: 0,
      productId: 0,
      title: "",
      description: "",
      routerLink: "/",
      hasMainMedia: false,
      mainMedia: {
        type: "image",
        img: { src: "", caption: "" },
        items: [],
      },
      mainImageSizes: [],
      hasSingleImage: false,
      hasSlideshow: false,
      sections: [],
      price: 0.0,
      priceGross: 0.0,
      options: [],
      selectedOptions: [],
      currency: "GBP",
      slug: "",
      hasSelectedAdd: false,
      updateable: false,
      errorMessages: [],
    };
  },
  computed: {
    ...mapGetters(["posts", "cart", "labels", "hasCart"]),
    wrapperClasses() {
      let cls = ["detail"];
      cls.push(this.slug);
      return cls;
    },
    wrapperId() {
      return "product-detail-" + this.productId;
    },
    available() {
      const { available } = this.product;
      return available !== false;
    },
    selectedItems() {
      let items = [];
      if (this.cart) {
        if (this.cart.items instanceof Array) {
          items = this.cart.items.filter(
            (item) => item.product.id.toString() === this.productId.toString()
          );
        }
      }
      return items;
    },
    quantity() {
      let qty = 0;
      if (this.selectedItems.length > 0) {
        qty = this.selectedItems
          .map((li) => {
            let q = 0;
            if (li.quantity) {
              q = li.quantity;
            }
            return q;
          })
          .reduce((a, b) => a + b, 0);
      }
      return qty;
    },
    hasOptions() {
      return this.options instanceof Array && this.options.length > 0;
    },
    addToCartText() {
      return this.addedToCart
        ? this.updateable
          ? this.labels.update_options
          : this.labels.added_to_cart
        : this.labels.add_to_cart;
    },
    addedToCart() {
      return this.quantity > 0;
    },
    buttonClasses() {
      let cls = [];
      if (this.addedToCart) {
        cls.push("added");
        if (this.quantity > 1) {
          cls.push("many");
        } else {
          cls.push("one");
        }
      }
      return cls;
    },
    hasSections() {
      return this.sections.length > 0;
    },
    speed() {
      return this.full ? 6000 : 0;
    },
    currentPrice() {
      return this.prependCurrency(this.recalcDisplayPrice(true));
    },
    basePrice() {
      return this.recalcDisplayPrice(false);
    },
  },
  watch: {
    selectedOptions(cVal) {
      this.updateable = false;
      if (cVal.length > 0 && this.addedToCart) {
        if (this.cart.items) {
          let options = this.fetchCurrentOptions();
          if (options) {
            let keys = Object.keys(options);
            let numKeys = keys.length;
            let numSame = 0;
            for (let i = 0; i < numKeys; i++) {
              let k = keys[i];
              let vl = options[k];
              if (i < this.selectedOptions.length) {
                if (vl === this.selectedOptions[i]) {
                  numSame++;
                }
              }
            }
            this.updateable = numSame !== numKeys;
          }
        }
      }
    },
    "cart.items"(newVal, oldVal) {
      if (
        newVal instanceof Array &&
        oldVal instanceof Array &&
        oldVal.length < 1 &&
        newVal.length > 0
      ) {
        let options = this.fetchCurrentOptions();
        this.options.forEach((opt, index) => {
          let k = opt.name;
          if (options[k]) {
            this.selectedOptions[index] = options[k];
          }
        });
        this.priceGross = 0;
        if (this.product.price) {
          this.price = this.product.price;
          this.priceGross = this.product.priceGross;
        }
      }
    },
  },
  created() {
    this.mapProduct();
  },
  methods: {
    ...mapActions(["updateCart"]),
    mapProduct() {
      if (this.product) {
        const product = this.product;
        this.mainImageSizes = config.imageStyles.wide2_41;
        if (product) {
          this.id = product.id;
          if (product.productId) {
            this.productId = product.productId;
          }
          if (product.title) {
            this.title = product.title;
          }
          if (product.routerLink) {
            this.routerLink = product.routerLink;
          }
          if (product.price) {
            this.price = product.price;
            this.priceGross = product.priceGross;
          }
          if (product.mainText) {
            this.description = product.mainText;
          }
          if (product.hasMainMedia) {
            this.mainMedia = product.mainMedia;
            this.hasMainMedia = product.hasMainMedia;
          }
          if (product.content) {
            if (product.content instanceof Array) {
              this.sections = product.content;
            }
          }
          if (product.options) {
            if (product.options instanceof Array) {
              this.options = product.options;
              this.selectedOptions = this.options.map((opt) => {
                switch (opt.type.toLowerCase()) {
                  case "checkbox":
                    return [];
                  default:
                    return "";
                }
              });
            }
          }
        }
      }
    },
    optionKey(prefix, productId, optionIndex, choiceIndex) {
      let parts = [prefix, productId, optionIndex];
      if (choiceIndex !== undefined && choiceIndex !== null) {
        parts.push(choiceIndex);
      }
      return parts.join("-");
    },
    showFull() {
      this.$parent.showFullProduct(this.routerLink);
    },
    addEcwidProductFirst(productId) {
      if (!this.addedToCart) {
        this.addEcwidProduct(productId);
      } else {
        if (this.options.length > 0) {
          if (this.quantity === 1) {
            let index = this.matchItemIndex(productId);
            if (index >= 0) {
              getCart().removeProduct(index);
              setTimeout(() => {
                this.addEcwidProduct(productId, 1);
              }, 750);
            }
          }
        }
      }
    },
    addEcwidProduct(productId, num) {
      if (hasEcwidCart()) {
        if (!num) {
          num = 1;
        }
        let options = this.checkOptions();
        if (this.errorMessages.length < 1) {
          getCart().addProduct({
            id: productId,
            quantity: num,
            options: options,
            callback: (success, product, cart) => {
              if (success) {
                this.updateCart(cart);
              }
            },
          });
        }
        this.hasSelectedAdd = true;
      }
    },
    preCheckOptions() {
      if (this.hasSelectedAdd) {
        setTimeout(this.checkOptions, 250);
      }
    },
    checkOptions() {
      let options = {};
      this.errorMessages = [];
      if (this.selectedOptions.length > 0) {
        this.selectedOptions.forEach((optVal, optIndex) => {
          if (optIndex < this.options.length) {
            let opt = this.options[optIndex];
            if (optVal.length > 0) {
              options[opt.name] = optVal;
            } else {
              this.errorMessages.push("Please select " + opt.name);
            }
          }
        });
      }
      return options;
    },
    subtractEcwidProduct(productId) {
      if (hasEcwidCart()) {
        let index = this.matchItemIndex(productId);
        if (index >= 0) {
          let { quantity } = this.cart.items[index];
          if (quantity > 0) {
            getCart().removeProduct(index);
            if (quantity > 1) {
              setTimeout(() => {
                this.addEcwidProduct(productId, this.quantity - 1);
              }, 250);
            }
          }
        }
      }
    },
    matchItemIndex(productId) {
      let index = -1;
      if (this.hasCart) {
        let itemIndex = this.cart.items
          .map((item) => {
            let pId = -1;
            if (item.product) {
              pId = item.product.id;
            }
            return pId;
          })
          .lastIndexOf(productId);
        if (itemIndex >= 0) {
          index = itemIndex;
        }
      }
      return index;
    },
    removeEcwidProduct(productId) {
      let index = -1;
      if (hasEcwidCart()) {
        let index = this.matchItemIndex(productId);
        if (index >= 0) {
          getCart().removeProduct(index);
          setTimeout(() => {
            getCart().get((data) => {
              this.updateCart(data);
            });
          }, 500);
        }
      }
      return index;
    },
    calcExtra(modifier) {
      let str = "";
      if (modifier) {
        let nVal = parseFloat(modifier);
        if (!isNaN(nVal)) {
          let rate = this.calcRate(this.product);
          if (!isNaN(rate)) {
            let prefix = nVal > 0 ? "+ " : "- ";
            str = prefix + this.prependCurrency(nVal * rate);
          }
        }
      }
      return str;
    },
    fetchCurrentOptions() {
      let opts = {};
      let itemIndex = this.cart.items
        .map((item) => item.product.id)
        .lastIndexOf(this.productId);
      if (itemIndex >= 0) {
        let { options } = this.cart.items[itemIndex];
        if (options !== null || options !== undefined) {
          opts = options;
        }
      }
      return opts;
    },
    recalcDisplayPrice(addTax) {
      let price = addTax ? this.priceGross : this.price;
      if (this.selectedOptions.length > 0) {
        let rate = addTax ? this.calcRate(this.product) : 1;
        this.selectedOptions.forEach((op, index) => {
          let choice = this.options[index].choices.find((c) => c.text === op);
          if (choice) {
            if (choice.priceModifier) {
              price += choice.priceModifier * rate;
            }
          }
        });
      }
      return price;
    },
  },
};
</script>
